import React, { useState } from 'react';
import { Button } from './ui/Button';
import { Input, TextArea } from './ui/Input';
import { Phone, Mail, MapPin, CheckCircle } from 'lucide-react';
export function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Name ist erforderlich';
    if (!formData.email.trim()) {
      newErrors.email = 'E-Mail ist erforderlich';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Bitte geben Sie eine gültige E-Mail ein';
    }
    if (!formData.message.trim()) newErrors.message = 'Nachricht ist erforderlich';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsSubmitting(false);
    setIsSuccess(true);
    setFormData({
      name: '',
      email: '',
      company: '',
      message: ''
    });
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  return <section id="contact" className="py-20 bg-gray-900 text-white" data-id="element-17">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-18">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20" data-id="element-19">
          {/* Contact Info */}
          <div className="w-full lg:w-1/3" data-id="element-20">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-id="element-21">
              Lassen Sie uns sprechen
            </h2>
            <p className="text-gray-300 mb-10 leading-relaxed" data-id="element-22">
              Bereit für authentische Bilder, die Ihr Business voranbringen?
              Schreiben Sie mir oder rufen Sie an für ein unverbindliches
              Erstgespräch.
            </p>

            <div className="space-y-6" data-id="element-23">
              <div className="flex items-start" data-id="element-24">
                <Phone className="h-6 w-6 text-[#D4A574] mr-4 mt-1" data-id="element-25" />
                <div data-id="element-26">
                  <p className="font-medium text-white" data-id="element-27">Telefon</p>
                  <a href="tel:+491234567890" className="text-gray-300 hover:text-[#D4A574] transition-colors" data-id="element-28">
                    +49 123 456 7890
                  </a>
                </div>
              </div>

              <div className="flex items-start" data-id="element-29">
                <Mail className="h-6 w-6 text-[#D4A574] mr-4 mt-1" data-id="element-30" />
                <div data-id="element-31">
                  <p className="font-medium text-white" data-id="element-32">E-Mail</p>
                  <a href="mailto:hallo@juliapfeifer.de" className="text-gray-300 hover:text-[#D4A574] transition-colors" data-id="element-33">
                    hallo@juliapfeifer.de
                  </a>
                </div>
              </div>

              <div className="flex items-start" data-id="element-34">
                <MapPin className="h-6 w-6 text-[#D4A574] mr-4 mt-1" data-id="element-35" />
                <div data-id="element-36">
                  <p className="font-medium text-white" data-id="element-37">Studio</p>
                  <p className="text-gray-300" data-id="element-38">
                    Musterstraße 12
                    <br data-id="element-39" />
                    10115 Berlin
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="w-full lg:w-2/3 bg-white rounded-lg p-8 text-gray-900" data-id="element-40">
            {isSuccess ? <div className="h-full flex flex-col items-center justify-center text-center py-12" data-id="element-41">
                <CheckCircle className="h-16 w-16 text-green-500 mb-4" data-id="element-42" />
                <h3 className="text-2xl font-bold mb-2" data-id="element-43">Vielen Dank!</h3>
                <p className="text-gray-600" data-id="element-44">
                  Ihre Nachricht wurde gesendet. Ich melde mich innerhalb von 24
                  Stunden bei Ihnen.
                </p>
                <Button variant="outline" className="mt-8" onClick={() => setIsSuccess(false)} data-id="element-45">
                  Neue Nachricht senden
                </Button>
              </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-46">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-47">
                  <Input label="Name *" name="name" value={formData.name} onChange={handleChange} error={errors.name} placeholder="Ihr Name" data-id="element-48" />
                  <Input label="Unternehmen" name="company" value={formData.company} onChange={handleChange} placeholder="Firmenname (optional)" data-id="element-49" />
                </div>

                <Input label="E-Mail *" name="email" type="email" value={formData.email} onChange={handleChange} error={errors.email} placeholder="ihre@email.de" data-id="element-50" />

                <TextArea label="Nachricht *" name="message" value={formData.message} onChange={handleChange} error={errors.message} placeholder="Erzählen Sie mir kurz von Ihrem Vorhaben..." rows={5} data-id="element-51" />

                <Button type="submit" size="lg" className="w-full" isLoading={isSubmitting} data-id="element-52">
                  Jetzt kostenloses Erstgespräch anfragen
                </Button>

                <p className="text-xs text-gray-500 text-center mt-4" data-id="element-53">
                  Mit dem Absenden stimmen Sie der Verarbeitung Ihrer Daten
                  gemäß der Datenschutzerklärung zu.
                </p>
              </form>}
          </div>
        </div>
      </div>
    </section>;
}